import { useCallback } from 'react';

import type { User } from 'firebase/auth';

import { analytics } from '../../services/analytics';
import type { AuthParams } from '../../services/auth';
import { auth } from '../../services/auth';
import { sendGoogleTagSignUpClick } from '../../services/googleTagEvent';
import { useStores } from '../../stores/useStores';
import { getErrorMessage } from '../../utils/errors';
import { getEnvironment } from '../../utils/getEnvironment';

export type AuthAction = 'log_in' | 'sign_up';
export type AuthProvider = 'apple' | 'google' | 'facebook' | 'email';

export interface Credentials {
  email: string;
  password: string;
}

export function useHandleAuth(onError: (error: string) => void, defaultNextStep?: () => void) {
  const { authStore, portalStore } = useStores();

  const landingKey = authStore.getLandingKey();

  const handleAuth = useCallback(
    async (
      params: AuthParams & { nextStep?: () => void },
    ): Promise<{
      success: boolean;
      error: string | null;
      user: User | null;
    }> => {
      auth.saveAuthParams(params);
      const { provider, action, credentials, nextStep: customNextStep } = params;
      const nextStep = customNextStep || defaultNextStep;

      try {
        auth.clearStatuses();

        if (authStore.isAuthenticationFlowDone) {
          authStore.setIsAuthenticationFlowDone(false);
        }

        authStore.setLastLoginTabId();
        if (action === 'sign_up') {
          sendGoogleTagSignUpClick({
            provider,
            email: credentials?.email,
          });
        }
        if (provider === 'email') {
          let email = credentials?.email || '';
          if (email && getEnvironment() === 'production') {
            const [left, right] = email.split('@');

            const isAllowedPlusInEmail = ['sunfltd.com', 'crowncoinscasino.com'].includes(right);

            if (!isAllowedPlusInEmail) {
              email = left.replace(/\+.+/, '+') + '@' + right;
            }
          }
          const data: Credentials = {
            email,
            password: credentials?.password || '',
          };
          let user;
          if (action === 'sign_up') {
            user = await authStore.signUpWithEmail(data);
          } else {
            user = await authStore.signInWithEmail(data);
          }
          if (!user.emailVerified && typeof nextStep === 'function') {
            nextStep();
          } else {
            authStore.setAuthCompleted();
            portalStore.closePortal();
          }
          return {
            success: true,
            error: null,
            user,
          };
        }
        const user = await authStore.authWithThirdPartyPlatform(provider);

        return {
          success: true,
          error: null,
          user,
        };
      } catch (e) {
        authStore.setIsAuthenticationFlowDone(true);
        const msg = getErrorMessage(e);

        if (action === 'sign_up') {
          void analytics.sendEvent('sign_up', {
            step_name: 'sign_up',
            event_action: 'validation',
            sign_up_platform: provider,
            is_succeeded: 0,
            user_id: '',
            landingKey,
          });
        }
        onError(msg);

        return {
          success: false,
          error: msg,
          user: null,
        };
      }
    },
    [authStore, defaultNextStep, portalStore, onError, landingKey],
  );

  return {
    handleAuth,
  };
}
